import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft,
  FolderOpen,
  Search,
  Filter,
  ChevronRight,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Row, Col, Button, Form, Table, Pagination, Card, Badge, InputGroup } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import './Dashboard.css';
import './CompanyProjects.css';

const CompanyRetailers = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { user } = useUser();
  
  const [company, setCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState(null); // 'custProjNo', 'projectName', 'tylerNumber', 'tylerProjectName', 'status'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const stateRestoreInProgress = useRef(false);
  const STORAGE_KEY_BASE = 'companyRetailersState';
  const hasRestoredState = useRef(false);

  const coerceNumber = (val, fallback) => {
    const n = typeof val === 'number' ? val : Number(val);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  // Fetch company data
  const fetchCompanyData = async () => {
    try {
      const response = await apiService.getCompanyById(companyId);
      
      if (response.success) {
        setCompany({
          id: response.data.id,
          identificationNumber: response.data.identificationNo || `COMP${response.data.id.toString().padStart(3, '0')}`,
          name: response.data.company || 'Unknown Company',
          industry: response.data.branch || 'Unknown Industry',
          status: response.data.active ? 'Active' : 'Inactive'
        });
      } else {
        setError(response.message || 'Failed to load company information');
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setError('Failed to load company information');
    }
  };

  // Fetch retailer projects for the company
  const fetchRetailerProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getProjectsByCompany(companyId);
      
      if (response.success) {
        // Filter only retailer projects
        const retailerProjects = response.data.filter(project => project.isRetailer);
        setProjects(retailerProjects);
      } else {
        setError(response.message || 'Failed to load retailer projects');
      }
    } catch (error) {
      console.error('Error fetching retailer projects:', error);
      setError('Failed to load retailer projects');
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top when component mounts or companyId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
      fetchRetailerProjects();
    }
  }, [companyId]);

  // Restore saved view state (per company) from session storage
  useEffect(() => {
    const storageKey = `${STORAGE_KEY_BASE}:${companyId}`;
    const savedRaw = sessionStorage.getItem(storageKey);
    if (!savedRaw) return;
    if (hasRestoredState.current) return;
    hasRestoredState.current = true;
    try {
      const saved = JSON.parse(savedRaw);
      stateRestoreInProgress.current = true;
      if (typeof saved.searchTerm === 'string') setSearchTerm(saved.searchTerm);
      if (typeof saved.filterStatus === 'string') setFilterStatus(saved.filterStatus);
      if (typeof saved.itemsPerPage === 'number') setItemsPerPage(saved.itemsPerPage);
      if (typeof saved.currentPage === 'number') setCurrentPage(saved.currentPage);
      if (typeof saved.sortColumn === 'string' || saved.sortColumn === null) setSortColumn(saved.sortColumn);
      if (typeof saved.sortDirection === 'string') setSortDirection(saved.sortDirection);
    } catch (err) {
      console.error('Failed to restore company retailers state', err);
    } finally {
      setTimeout(() => {
        stateRestoreInProgress.current = false;
      }, 0);
    }
  }, [companyId]);

  // Persist view state for this company in session storage
  useEffect(() => {
    const storageKey = `${STORAGE_KEY_BASE}:${companyId}`;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          searchTerm: typeof searchTerm === 'string' ? searchTerm : '',
          filterStatus: typeof filterStatus === 'string' ? filterStatus : 'all',
          itemsPerPage: coerceNumber(itemsPerPage, 25),
          currentPage: coerceNumber(currentPage, 1),
          sortColumn,
          sortDirection
        })
      );
    } catch (err) {
      console.error('Failed to persist company retailers state', err);
    }
  }, [
    companyId,
    searchTerm,
    filterStatus,
    itemsPerPage,
    currentPage,
    sortColumn,
    sortDirection
  ]);

  // Filter projects
  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.custProjNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.projectNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.tylerProjectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.tylerNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Handle status filtering with proper mapping
    let matchesStatus = true;
    if (filterStatus !== 'all') {
      const projectStatus = project.status.toLowerCase();
      if (filterStatus === 'retail') {
        // Match both 'retail' and 'retail w/ detailing' for retail filter
        matchesStatus = projectStatus === 'retail' || projectStatus === 'retail w/ detailing';
      } else if (filterStatus === 'closed') {
        // Match 'closed', 'retail-dead', and 'invoiced' for closed filter
        matchesStatus = projectStatus === 'closed' || projectStatus === 'retail-dead' || projectStatus === 'invoiced';
      } else {
        matchesStatus = projectStatus === filterStatus.toLowerCase();
      }
    }
    
    return matchesSearch && matchesStatus;
  });

  // Helper function to check if status is retail
  const isRetailStatus = (status) => {
    const statusLower = (status || '').toLowerCase();
    return statusLower === 'retail' || statusLower === 'retail w/ detailing';
  };

  // Helper function to check if status is closed
  const isClosedStatus = (status) => {
    const statusLower = (status || '').toLowerCase();
    return statusLower === 'closed' || statusLower === 'retail-dead' || statusLower === 'invoiced';
  };

  // Helper function to get status sort order for retail (Retail = 1, Closed = 2, Other = 3)
  const getRetailStatusSortOrder = (status) => {
    if (isRetailStatus(status)) {
      return 1;
    }
    if (isClosedStatus(status)) {
      return 2;
    }
    return 3;
  };

  // Helper function to parse Customer # for numeric sorting
  const parseCustomerNumber = (custProjNo) => {
    if (!custProjNo || custProjNo.trim() === '') {
      return { isEmpty: true, numericValue: 0 };
    }
    // Extract numeric part from the string (e.g., "T00022" -> 22)
    const numericMatch = custProjNo.match(/\d+/);
    if (numericMatch) {
      return { isEmpty: false, numericValue: parseInt(numericMatch[0], 10) };
    }
    // If no numeric part, use string comparison
    return { isEmpty: false, numericValue: 0, stringValue: custProjNo.toLowerCase() };
  };

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Reset to page 1 when search or filter changes (unless restoring)
  useEffect(() => {
    if (stateRestoreInProgress.current) return;
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Get sort icon for a column
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={16} className="me-1 text-muted" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp size={16} className="me-1 text-primary" />
      : <ArrowDown size={16} className="me-1 text-primary" />;
  };

  // Sort projects: Retail first, then Closed, then Customer # for Retail items (or manual sort)
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    // If manual sort is selected, use that
    if (sortColumn) {
      let aValue, bValue;

      switch (sortColumn) {
        case 'custProjNo':
          aValue = (a.custProjNo || '').toLowerCase();
          bValue = (b.custProjNo || '').toLowerCase();
          break;
        case 'projectName':
          aValue = (a.projectName || '').toLowerCase();
          bValue = (b.projectName || '').toLowerCase();
          break;
        case 'tylerNumber':
          aValue = (a.tylerNumber || '').toLowerCase();
          bValue = (b.tylerNumber || '').toLowerCase();
          break;
        case 'tylerProjectName':
          aValue = (a.tylerProjectName || '').toLowerCase();
          bValue = (b.tylerProjectName || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    }

    // Default sort: Retail first, then Closed, then Customer # for Retail items
    const aStatusOrder = getRetailStatusSortOrder(a.status);
    const bStatusOrder = getRetailStatusSortOrder(b.status);

    // First sort by status order (Retail > Closed > Other)
    if (aStatusOrder !== bStatusOrder) {
      return aStatusOrder - bStatusOrder;
    }

    // If both are Retail, sort by Customer # (lowest to highest, empty first)
    if (aStatusOrder === 1 && bStatusOrder === 1) {
      const aCustNo = parseCustomerNumber(a.custProjNo);
      const bCustNo = parseCustomerNumber(b.custProjNo);

      // Empty values come first
      if (aCustNo.isEmpty && !bCustNo.isEmpty) return -1;
      if (!aCustNo.isEmpty && bCustNo.isEmpty) return 1;
      if (aCustNo.isEmpty && bCustNo.isEmpty) return 0;

      // Both have numeric values, sort numerically
      if (aCustNo.numericValue !== bCustNo.numericValue) {
        return aCustNo.numericValue - bCustNo.numericValue;
      }

      // If numeric values are equal or both are non-numeric, sort by string
      if (aCustNo.stringValue && bCustNo.stringValue) {
        return aCustNo.stringValue.localeCompare(bCustNo.stringValue);
      }
      return (a.custProjNo || '').toLowerCase().localeCompare((b.custProjNo || '').toLowerCase());
    }

    // For non-Retail items with same status, maintain original order
    return 0;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProjects = sortedProjects.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedProjects.length / itemsPerPage);

  const handleEditProject = (projectId) => {
    // Find the project data to pass in navigation state
    const project = projects.find(p => p.id === projectId);
    navigate(`/company/${companyId}/project/${projectId}`, {
      state: {
        project: project,
        company: company
      }
    });
  };

  const handleViewProjectDetails = (project) => {
    navigate(`/company/${companyId}/project/${project.id}`, {
      state: {
        project: project,
        company: company
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">Unknown</Badge>;
    
    switch (status.toLowerCase()) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'invoiced':
        return <Badge bg="secondary">Closed</Badge>;
      case 'closed':
        return <Badge bg="secondary">Closed</Badge>;
      case 'sold':
        return <Badge bg="primary">Sold</Badge>;
      case 'final':
        return <Badge bg="info">Final</Badge>;
      case 'lost':
        return <Badge bg="danger">Lost</Badge>;
      case 'no bid':
        return <Badge bg="warning" text="dark">No Bid</Badge>;
      case 'retail-dead':
        return <Badge bg="secondary">Closed</Badge>;
      case 'retail':
        return <Badge bg="warning" text="dark">Retail</Badge>;
      case 'retail w/ detailing':
        return <Badge bg="warning" text="dark">Retail</Badge>;
      case 'not built':
        return <Badge bg="outline-secondary">Not Built</Badge>;
      default:
        return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="company-projects-wrapper">
        <div className="modern-dashboard">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading retailer projects...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-projects-wrapper">
        <div className="modern-dashboard">
          <div className="alert alert-danger" role="alert">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div>
                <strong>Error loading retailer projects:</strong> {error}
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  className="ms-3"
                  onClick={fetchRetailerProjects}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-projects-wrapper">
      <div className="modern-dashboard">
        {/* Header */}
        <div className="dashboard-header-section mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                {user?.role === 'superAdmin' && (
                  <Button 
                    variant="link" 
                    className="p-0 me-3 text-decoration-none text-dark"
                    style={{ color: '#1f2937' }}
                    onClick={() => navigate('/companies')}
                  >
                    <ArrowLeft size={20} />
                  </Button>
                )}
                <div>
                  <h1 className="dashboard-title mb-1">{company?.name || 'Loading...'}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="nav-tabs-custom">
          <div className="nav-item">
            <span 
              className="nav-link"
              onClick={() => navigate(`/company/${companyId}/projects`)}
              style={{ cursor: 'pointer' }}
            >
              Projects
            </span>
          </div>
          <div className="nav-item active">
            <span className="nav-link active">Retail</span>
          </div>
          <div className="nav-item">
            <span 
              className="nav-link"
              onClick={() => navigate(`/company/${companyId}/users`)}
              style={{ cursor: 'pointer' }}
            >
              Users
            </span>
          </div>
        </div>

        {/* Filters and Search */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={9}>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search retailer projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="retail">Retail</option>
                  <option value="closed">Closed</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Projects Table */}
        {!loading && !error && (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Retail Sales List</h5>
              <div className="d-flex align-items-center gap-3">
                <span className="text-muted">{sortedProjects.length} retailer projects found</span>
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={fetchRetailerProjects}
                  disabled={loading}
                >
                  <RefreshCw size={16} className={`me-2 ${loading ? 'spin' : ''}`} />
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="modern-table mb-0">
                <thead>
                  <tr>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('tylerNumber')}
                    >
                      <div className="d-flex align-items-center">
                        {getSortIcon('tylerNumber')}
                        Retail #
                      </div>
                    </th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('tylerProjectName')}
                    >
                      <div className="d-flex align-items-center">
                        {getSortIcon('tylerProjectName')}
                        Description
                      </div>
                    </th>
                    <th 
                      style={{ cursor: 'pointer', textAlign: 'right' }}
                      onClick={() => handleSort('status')}
                    >
                      <div className="d-flex align-items-center justify-content-end">
                        {getSortIcon('status')}
                        Status
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProjects.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-5">
                        <FolderOpen size={48} className="text-muted mb-3" />
                        <h5 className="text-muted">No retailer projects found</h5>
                        <p className="text-muted">This company doesn't have any retailer projects yet.</p>
                      </td>
                    </tr>
                  ) : (
                    currentProjects.map((project) => {
                      const displayCustProjNo = project.custProjNo || project.tylerNumber || '';
                      const displayProjectName = project.projectName || project.tylerProjectName || '';
                      const useFallbackCust = !project.custProjNo && !!project.tylerNumber;
                      const useFallbackProject = !project.projectName && !!project.tylerProjectName;
                      const grayStyle = { color: '#D3D3D3' };
                      return (
                        <tr 
                          key={project.id} 
                          className="project-row"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleEditProject(project.id)}
                        >
                          <td>
                            <div className="d-flex align-items-center">
                              <ChevronRight size={16} className="me-2 text-muted" />
                              {project.tylerNumber || ''}
                            </div>
                          </td>
                          <td>{project.tylerProjectName || ''}</td>
                          <td style={{ textAlign: 'right' }}>{getStatusBadge(project.status)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Pagination */}
        {(totalPages > 1 || true) && (
          <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
            {/* Items per page selector */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                Show:
              </span>
              <Form.Select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
                size="sm"
                style={{ width: 'auto', minWidth: '80px' }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
              <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                per page
              </span>
            </div>
            
            {/* Pagination controls */}
            {totalPages > 1 && (
              <Pagination className="mb-0">
              <Pagination.Prev 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === currentPage}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next 
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              />
              </Pagination>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyRetailers;
